/**
 * Stable, URL-safe encoding of a routing context (the 6 dimensions that select
 * one priority list). Used as the `:contextId` route param so the editor can be
 * opened directly from the coverage map by deep link.
 *
 * Encoding is a fixed-order, delimiter-joined tuple. The pipe `|` cannot appear
 * in any dimension value (tokens are slugs, the rest are enums), so a plain
 * `encodeURIComponent` of the joined string round-trips losslessly.
 *
 * Ported verbatim from the prototype — framework-agnostic.
 */

import type { RoutingContext, Direction, Source } from './types'

const SEP = '|'
const ORDER: Array<keyof RoutingContext> = [
  'merchantToken',
  'paymentMethodType',
  'currency',
  'direction',
  'externalUserFlag',
  'source',
]

/** Encode a context to a single URL path segment. */
export function encodeContextId(ctx: RoutingContext): string {
  const tuple = ORDER.map((k) => String(ctx[k])).join(SEP)
  return encodeURIComponent(tuple)
}

/** Decode a context id back to a {@link RoutingContext}, or null if malformed. */
export function decodeContextId(id: string): RoutingContext | null {
  let raw: string
  try {
    raw = decodeURIComponent(id)
  } catch {
    return null
  }
  const parts = raw.split(SEP)
  if (parts.length !== ORDER.length) return null
  const [merchantToken, paymentMethodType, currency, direction, externalUserFlag, source] = parts
  if (!merchantToken || !paymentMethodType) return null
  if (direction !== 'PAYMENT' && direction !== 'PAYOUT') return null
  if (source !== 'MERCHANT' && source !== 'PLATFORM') return null
  return {
    merchantToken,
    paymentMethodType,
    currency,
    direction: direction as Direction,
    externalUserFlag,
    source: source as Source,
  }
}

/** Stable equality for two contexts (by the 6 dimensions). */
export function sameContext(a: RoutingContext, b: RoutingContext): boolean {
  return ORDER.every((k) => a[k] === b[k])
}
